import { ThemeProvider } from 'emotion-theming'
import { parseAllowLinks, parseEmbedTitle } from 'markdown/render'
import { Video } from 'markdown/render/elements/media'
import { videoDimensions } from 'markdown/render/mediaUrl'
import * as Moment from 'moment'
import * as React from 'react'

import { Twemoji } from 'emoji'
import { Content, Root, Title, Wrapper } from './elements'
import { Author, AuthorIcon, AuthorName } from './elements/author'
import { ColorPill } from './elements/colorpill'
import { Description } from './elements/description'
import { Field, FieldName, Fields, FieldValue } from './elements/fields'
import { Footer, FooterIcon, FooterText } from './elements/footer'
import { Thumbnail } from './elements/thumbnail'
import InviteCard from './InviteCard'
import { isDiscordInviteUrl } from './inviteUrl'

const parseEmojis = text =>
  text && typeof text === 'string' ? (
    <Twemoji resolveNames>{text}</Twemoji>
  ) : text instanceof Array ? (
    text.map(
      (part, i) =>
        typeof part === 'string' ? (
          <Twemoji resolveNames key={i + part}>
            {part}
          </Twemoji>
        ) : (
          part
        )
    )
  ) : (
    text
  )

const Link = ({ children, ...props }) => (
  <a target="_blank" rel="noreferrer" {...props}>
    {children}
  </a>
)

const EmbedTitle = ({ title, url, mentions }) =>
  title ? (
    url ? (
      <Title>
        <Link href={url}>{parseEmojis(parseEmbedTitle(title, true, { mentions }))}</Link>
      </Title>
    ) : (
      <Title>{parseEmojis(parseEmbedTitle(title, true, { mentions }))}</Title>
    )
  ) : null

const EmbedDescription = ({ content, mentions }) => (content ? <Description>{parseEmojis(parseAllowLinks(content, false, { mentions }))}</Description> : null)

const EmbedAuthor = ({ name, url, iconURL }) => {
  if (!name) {
    return null
  }

  let authorName
  if (name) {
    authorName = <AuthorName>{name}</AuthorName>
    if (url) {
      authorName = (
        <AuthorName>
          <Link href={url}>{name}</Link>
        </AuthorName>
      )
    }
  }

  const authorIcon = iconURL ? <AuthorIcon src={iconURL} /> : null

  return (
    <Author>
      {authorIcon}
      {parseEmojis(authorName)}
    </Author>
  )
}

const EmbedField = ({ name, value, inline, mentions }) => {
  if (!name && !value) {
    return null
  }

  const fieldName = name ? <FieldName>{parseEmojis(parseEmbedTitle(name, true, { mentions }))}</FieldName> : null
  const fieldValue = value ? <FieldValue>{parseEmojis(parseAllowLinks(value, false, { mentions }))}</FieldValue> : null

  return (
    <Field inline={inline}>
      {fieldName}
      {fieldValue}
    </Field>
  )
}

const EmbedThumbnail = ({ proxyURL, url, height, width }) => {
  const src = proxyURL || url
  return src ? <Thumbnail src={src} height={height} width={width} maxWidth={80} maxHeight={80} /> : null
}

const EmbedImage = ({ proxyURL, url, height, width }) => {
  const src = proxyURL || url
  return src ? (
    <span>
      <Thumbnail rich src={src} height={height} width={width} maxWidth={400} maxHeight={300} />
    </span>
  ) : null
}

const EmbedFooter = ({ timestamp, text, proxyIconUrl }) => {
  if (!text && !timestamp) {
    return null
  }

  // pass null, since undefined will make moment(...) return the current date/time
  let time = Moment(timestamp !== undefined ? timestamp : null)

  const footerText = [text, time.isValid() ? time.calendar() : null].filter(Boolean).join(' • ')

  const footerIcon = text && proxyIconUrl ? <FooterIcon src={proxyIconUrl} /> : null

  return (
    <Footer>
      {footerIcon}
      <FooterText>{parseEmojis(footerText)}</FooterText>
    </Footer>
  )
}

const EmbedFields = ({ fields, mentions }) => {
  if (!fields) {
    return null
  }

  return <Fields>{fields.map((f, i) => <EmbedField key={i} {...f} mentions={mentions} />)}</Fields>
}

const Embed = ({ color, author, title, url, description, fields, thumbnail, image, timestamp, footer, mentions = null, ...embed }) =>
  embed.type === 'gifv' && embed.video && embed.video.url ? (
    <Video src={embed.video.url} {...videoDimensions(embed.video.width, embed.video.height)} />
  ) : isDiscordInviteUrl(url) ? (
    <InviteCard title={title} url={url} description={description} thumbnail={thumbnail} fields={fields} />
  ) : (
    <ThemeProvider
      theme={theme => ({
        ...(theme as {}),
        embed
      })}
    >
      <Root>
        <ColorPill bgcolor={color} />
        <Wrapper>
          <Content>
            <div>
              <EmbedAuthor {...author} />
              <EmbedTitle title={title} url={url} mentions={mentions} />
              <EmbedDescription content={description} mentions={mentions} />
              <EmbedFields fields={fields} mentions={mentions} />
            </div>
            <EmbedThumbnail {...thumbnail} />
          </Content>
          <EmbedImage {...image} />
          <EmbedFooter timestamp={timestamp} {...footer} />
        </Wrapper>
      </Root>
    </ThemeProvider>
  )

export default Embed



// WEBPACK FOOTER //
// ./src/components/Messages/Message/Embed/index.tsx